import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { dollarsToCents, apiError } from "@/lib/setupValues";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Power, PowerOff, Save, X, Edit2 } from "lucide-react";

const EMPTY_FORM = {
  name: "",
  power_kw: "",  // must be entered explicitly at setup — no assumed default
  engraving_speed_mm_min: "",
  enabled: true,
  applicable_processing_classes: [],
  source_version: "user-entered",
};

export default function MachinesPage({setupMode=false}) {
  const { session } = useAuth();
  const isAdmin = session?.user?.role === "admin";

  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError,setLoadError]=useState('');
  const [editing, setEditing] = useState(null); // machine id or "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [toggling,setToggling]=useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await api.get("/machines");
      setMachines(data);
    } catch(e) {
      setLoadError(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY_FORM);
  };
  const startEdit = (m) => {
    if(toggling===m.id)return;
    setEditing(m.id);
    setForm({
      name: m.name,
      machine_hourly_rate_cents: m.machine_hourly_rate_cents==null?"":String(m.machine_hourly_rate_cents/100),
      rapid_speed_mm_min: m.rapid_speed_mm_min??"",
      linear_acceleration_mm_s2: m.linear_acceleration_mm_s2??"",
      radial_acceleration_mm_s2: m.radial_acceleration_mm_s2??"",
      job_changeover_minutes: m.job_changeover_minutes??"",
      technology_change_minutes: m.technology_change_minutes??"",
      mixed_gas_cutting: m.mixed_gas_cutting??false,
      air_cutting: m.air_cutting??false,
      max_thickness_mm: m.max_thickness_mm||{},
      power_kw: m.power_kw,
      engraving_speed_mm_min: m.engraving_speed_mm_min??"",
      enabled: m.enabled,
      applicable_processing_classes: m.applicable_processing_classes || [],
      source_version: m.source_version || "user-entered",
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY_FORM); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const powerNum = Number(form.power_kw);
    if (form.power_kw === "" || !Number.isFinite(powerNum) || powerNum <= 0) {
      return toast.error("Power (kW) is required and must be > 0 — enter your machine's actual power");
    }
    if (!form.applicable_processing_classes.length) {
      return toast.error("At least one processing class required (e.g. laser-fibre)");
    }
    setSaving(true);
    try {
      const body = { ...form, power_kw: powerNum, engraving_speed_mm_min:form.engraving_speed_mm_min===''?null:Number(form.engraving_speed_mm_min) };
      for (const key of ['machine_hourly_rate_cents','rapid_speed_mm_min','linear_acceleration_mm_s2','radial_acceleration_mm_s2','job_changeover_minutes','technology_change_minutes']) {
        if(form[key]!==undefined) {body[key]=form[key]===''?null:key==='machine_hourly_rate_cents'?dollarsToCents(form[key],'Machine rate'):Number(form[key]);if(body[key]!==null&&(!Number.isFinite(body[key])||body[key]<0)) throw new Error('Enter valid non-negative machine values');}
      }
      for(const key of ['mixed_gas_cutting','air_cutting','max_thickness_mm']) if(form[key]!==undefined)body[key]=key==='max_thickness_mm'?Object.fromEntries(Object.entries(form[key]).map(([family,value])=>[family,value==null||value===''?null:Number(value)])):form[key];
      if (editing === "new") {
        await api.post("/machines", body);
        toast.success(`Machine "${form.name}" created`);
      } else {
        await api.patch(`/machines/${editing}`, body);
        toast.success(`Machine "${form.name}" updated`);
      }
      cancel();
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (m) => {
    if(toggling!==null)return;
    setToggling(m.id);
    try {
      await api.patch(`/machines/${m.id}`, {
        name: m.name,
        power_kw: m.power_kw, enabled: !m.enabled,
      });
      toast.success(`${m.name}: ${!m.enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setToggling(null);
    }
  };

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold">Machines</h1>
        {isAdmin && editing == null && (
          <button
            data-testid="new-machine-btn"
            onClick={startNew}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm px-3 py-1.5 rounded-md"
          >
            <Plus className="w-4 h-4" /> New machine
          </button>
        )}
      </div>
      

      {editing != null && isAdmin && (
        <div data-testid="machine-form" className="mb-6 bg-white border-2 border-slate-900 rounded-lg p-5">
          <h2 className="text-lg font-semibold mb-4">{editing === "new" ? "New machine" : "Edit machine"}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Name">
              <input
                data-testid="machine-name-input"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Trumpf 6kW fibre — bay 2"
              />
            </Field>
            <Field label="Power (kW)">
              <input
                data-testid="machine-power-input"
                type="number"
                step="0.1"
                min="0.1"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm numeric"
                value={form.power_kw}
                onChange={(e) => setForm({ ...form, power_kw: e.target.value })}
                placeholder="e.g. 6"
              />
              
            </Field>
            <Field label={setupMode?'Laser type':'Processing classes (comma-separated)'}>
              {setupMode?<select className="w-full px-3 py-2 border border-slate-300 rounded text-sm" aria-label="Laser type" value={form.applicable_processing_classes.join(',')} onChange={e=>setForm({...form,applicable_processing_classes:e.target.value?[e.target.value]:[]})}><option value="">Choose laser type</option><option value="laser-fibre">Fibre laser</option><option value="laser-co2">CO₂ laser</option>{form.applicable_processing_classes.length>0&&!['laser-fibre','laser-co2'].includes(form.applicable_processing_classes.join(','))&&<option value={form.applicable_processing_classes.join(',')}>{form.applicable_processing_classes.join(',')}</option>}</select>:<>
              <input
                data-testid="machine-classes-input"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                value={form.applicable_processing_classes.join(", ")}
                onChange={(e) => setForm({
                  ...form,
                  applicable_processing_classes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                })}
                placeholder="e.g. laser-fibre"
              />
              </>}
            </Field>
            {!setupMode&&<Field label="Engraving speed (mm/min)"><input className="w-full px-3 py-2 border border-slate-300 rounded text-sm" type="number" min=".001" step="any" value={form.engraving_speed_mm_min} placeholder="Enter to enable engraving on this machine" onChange={e=>setForm({...form,engraving_speed_mm_min:e.target.value})}/></Field>}
            
          </div>
          <div className="mb-5 grid gap-4 md:grid-cols-3" style={setupMode?{display:"none"}:undefined}><label className="block text-xs text-slate-600">Machine rate / hr ($)<input aria-label="Machine rate / hr ($)" type="number" min="0" step="any" className="mt-1 block w-full rounded border p-2" value={form.machine_hourly_rate_cents??''} onChange={e=>setForm({...form,machine_hourly_rate_cents:e.target.value})}/></label><label className="block text-xs text-slate-600">Rapid speed (mm/min)<input aria-label="Rapid speed (mm/min)" type="number" min="0" step="any" className="mt-1 block w-full rounded border p-2" value={form.rapid_speed_mm_min??''} onChange={e=>setForm({...form,rapid_speed_mm_min:e.target.value})}/></label><label className="block text-xs text-slate-600">Linear acceleration (mm/sec²)<input aria-label="Linear acceleration (mm/sec²)" type="number" min="0" step="any" className="mt-1 block w-full rounded border p-2" value={form.linear_acceleration_mm_s2??''} onChange={e=>setForm({...form,linear_acceleration_mm_s2:e.target.value})}/></label><label className="block text-xs text-slate-600">Radial acceleration (mm/sec²)<input aria-label="Radial acceleration (mm/sec²)" type="number" min="0" step="any" className="mt-1 block w-full rounded border p-2" value={form.radial_acceleration_mm_s2??''} onChange={e=>setForm({...form,radial_acceleration_mm_s2:e.target.value})}/></label><label className="block text-xs text-slate-600">Job changeover (min)<input aria-label="Job changeover (min)" type="number" min="0" step="any" className="mt-1 block w-full rounded border p-2" value={form.job_changeover_minutes??''} onChange={e=>setForm({...form,job_changeover_minutes:e.target.value})}/></label><label className="block text-xs text-slate-600">Technology / nozzle change (min)<input aria-label="Technology / nozzle change (min)" type="number" min="0" step="any" className="mt-1 block w-full rounded border p-2" value={form.technology_change_minutes??''} onChange={e=>setForm({...form,technology_change_minutes:e.target.value})}/></label><label className="block text-xs text-slate-600">Maximum Carbon Steel thickness (mm)<input type="number" min="0" step="any" className="mt-1 block w-full rounded border p-2" value={form.max_thickness_mm?.['Carbon Steel']??''} onChange={e=>setForm({...form,max_thickness_mm:{...form.max_thickness_mm,'Carbon Steel':e.target.value}})}/></label><label className="block text-xs text-slate-600">Maximum Stainless Steel thickness (mm)<input type="number" min="0" step="any" className="mt-1 block w-full rounded border p-2" value={form.max_thickness_mm?.['Stainless Steel']??''} onChange={e=>setForm({...form,max_thickness_mm:{...form.max_thickness_mm,'Stainless Steel':e.target.value}})}/></label><label className="block text-xs text-slate-600">Maximum Aluminium thickness (mm)<input type="number" min="0" step="any" className="mt-1 block w-full rounded border p-2" value={form.max_thickness_mm?.['Aluminium']??''} onChange={e=>setForm({...form,max_thickness_mm:{...form.max_thickness_mm,'Aluminium':e.target.value}})}/></label><div className="md:col-span-3 flex flex-wrap items-center gap-6 pt-2"><Field label="Enabled">
              <label className="inline-flex items-center gap-2 text-sm mt-2">
                <input
                  data-testid="machine-enabled-input"
                  type="checkbox"
                  checked={form.enabled}
                  onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                />
                {form.enabled ? "In service" : "Out of service"}
              </label>
            </Field><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.mixed_gas_cutting} onChange={e=>setForm({...form,mixed_gas_cutting:e.target.checked})}/>Mixed gas cutting</label><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.air_cutting} onChange={e=>setForm({...form,air_cutting:e.target.checked})}/>Air cutting</label></div></div><div className="flex gap-2 mt-5">
            <button
              data-testid="save-machine-btn"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm px-3 py-1.5 rounded-md"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
            </button>
            <button
              data-testid="cancel-machine-btn"
              onClick={cancel}
              className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-sm px-3 py-1.5 rounded-md"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {!isAdmin && (
        <div className="mb-4 text-sm bg-amber-50 border border-amber-200 rounded px-3 py-2 text-amber-800">
          Read-only — you need the <span className="font-mono">admin</span> role to edit machines.
        </div>
      )}

      <div className="space-y-3">
        {loading && <div className="text-slate-500 text-sm">loading…</div>}
        {loadError&&<div role="alert" className="rounded border border-red-200 bg-red-50 p-4 text-sm">{loadError}<button className="ml-3 underline" onClick={load}>Retry</button></div>}
        {!loading && !loadError && machines.length === 0 && (
          <div data-testid="no-machines-empty" className="bg-white border border-slate-200 rounded p-6 text-center text-slate-500 text-sm">
            Add and enable a machine, then add its cutting rates before pricing laser-cut parts.
          </div>
        )}
        {machines.map((m) => (
          <div key={m.id} data-testid={`machine-row-${m.id}`} className={`bg-white border rounded-lg p-4 flex flex-wrap items-center gap-4 ${m.enabled ? "border-slate-200" : "border-rose-200 bg-rose-50/40"}`}>
            <div className="flex-1 min-w-[240px]">
              <div className="text-base font-semibold text-slate-900">{m.name}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                <span className="numeric font-medium text-slate-700">{m.power_kw} kW</span>
                <span className="mx-2 text-slate-300">·</span>
                {(m.applicable_processing_classes || []).join(", ") || "no classes"}
              </div>
            </div>
            <div>
              <span
                data-testid={`machine-status-${m.id}`}
                className={`text-[11px] px-2 py-0.5 rounded-full border ${
                  m.enabled ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : "bg-rose-50 text-rose-800 border-rose-200"
                }`}
              >
                {m.enabled ? "enabled" : "disabled"}
              </span>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <button
                  data-testid={`edit-machine-${m.id}`}
                  disabled={toggling===m.id}
                  onClick={() => startEdit(m)}
                  className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 inline-flex items-center gap-1"
                >
                  <Edit2 className="w-3 h-3" /> Edit
                </button>
                <button
                  data-testid={`toggle-machine-${m.id}`}
                  disabled={toggling!==null}
                  onClick={() => toggleEnabled(m)}
                  className={`text-xs px-2 py-1 rounded border inline-flex items-center gap-1 ${
                    m.enabled ? "border-rose-300 text-rose-700 hover:bg-rose-50"
                              : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  }`}
                >
                  {m.enabled ? <PowerOff className="w-3 h-3" /> : <Power className="w-3 h-3" />}
                  {m.enabled ? "Disable" : "Enable"}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  const Wrapper=label==='Enabled'?'div':'label';
  return (
    <Wrapper className="block">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      {children}
    </Wrapper>
  );
}
